import ColumnHeader from './ColumnHeader';
import ColumnFooter from './ColumnFooter';
import { DND_TYPES } from './types';
import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useConfigs } from './lib/config-context';
import ColumnLoadMoreWrapper from './ColumnLoadMoreWrapper';
import styled from 'styled-components';
import { COLLAPSED_COLUMN_WIDTH, COLUMN_WIDTH } from './constants';
import CollapsedColumnContent from './CollapsedColumnContent';
import ColumnContent from './ColumnContent';
import { Droppable } from 'react-beautiful-dnd';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledColumnContainer = styled.div.withConfig({
  displayName: "Column__StyledColumnContainer",
  componentId: "sc-1ko651g-0"
})(({
  theme,
  isBoardEmpty,
  collapsed
}) => `
  min-inline-size: ${collapsed ? COLLAPSED_COLUMN_WIDTH : COLUMN_WIDTH}px;
  max-inline-size: ${collapsed ? COLLAPSED_COLUMN_WIDTH : COLUMN_WIDTH}px;
  border-left: ${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']};
  border-top: ${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']};
  border-bottom: ${isBoardEmpty ? 'none' : `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`};

  &:last-child {
    border-right: ${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']};
  }
`);
const StyleColumnBody = styled.div.withConfig({
  displayName: "Column__StyleColumnBody",
  componentId: "sc-1ko651g-1"
})(["height:100%;overflow-y:auto;background-color:", ";"], ({
  theme,
  columnBodyBackgroundColor
}) => theme.color[columnBodyBackgroundColor]);
const ColumnHeaderWrapper = /*#__PURE__*/memo(({
  columnData,
  collapsed,
  onColumnCollapse,
  onHovered,
  isBoardEmpty,
  backgroundColor
}) => {
  return /*#__PURE__*/_jsx(ColumnHeader, {
    columnData: columnData,
    collapsed: collapsed,
    onColumnCollapse: onColumnCollapse,
    onHovered: onHovered,
    isBoardEmpty: isBoardEmpty,
    backgroundColor: backgroundColor
  });
});
const Column = ({
  columnData,
  isBoardEmpty,
  shouldRenderCards
}) => {
  const {
    onColumnMounted,
    onColumnCollapse
  } = useConfigs();
  const [hovered, setHovered] = useState(false);
  const collapsed = useMemo(() => {
    if (isBoardEmpty) {
      return false;
    }
    return columnData.collapsed || false;
  }, [columnData, isBoardEmpty]);
  const onCollapse = useMemo(() => {
    if (isBoardEmpty) {
      return undefined;
    }
    return onColumnCollapse;
  }, [onColumnCollapse, isBoardEmpty]);
  const shouldCollapse = useMemo(() => {
    return collapsed && typeof onCollapse === 'function';
  }, [collapsed, onCollapse]);
  useEffect(() => {
    if (onColumnMounted) {
      onColumnMounted(columnData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const columnBackgroundColor = useCallback(isDraggingOver => {
    if (isDraggingOver && collapsed) {
      return 'fill-field-hover';
    }
    if (hovered && collapsed) {
      return 'fill-field-hover';
    }
    if (collapsed) {
      return 'fill-surface-raised';
    }
    if (hovered) {
      return 'fill-field-hover';
    }
    return 'fill-surface-raised';
  }, [collapsed, hovered]);
  const columnBodyBackgroundColor = useCallback(isDraggingOver => {
    if (isDraggingOver && collapsed) {
      return 'fill-field-hover';
    }
    if (hovered && collapsed) {
      return 'fill-field-hover';
    }
    if (isDraggingOver) {
      return 'fill-field-hover';
    }
    if (collapsed) {
      return 'fill-surface-raised';
    }
    return 'fill-surface-default';
  }, [collapsed, hovered]);
  const footer = useMemo(() => /*#__PURE__*/_jsx(ColumnFooter, {
    columnData: columnData
  }), [columnData]);
  const loadMoreComponent = useMemo(() => /*#__PURE__*/_jsx(ColumnLoadMoreWrapper, {
    columnData: columnData,
    shouldRenderCards: shouldRenderCards
  }), [shouldRenderCards, columnData]);
  const handleHover = useCallback(v => {
    setHovered(v);
  }, []);

  //@bend-expect trellis-tokenized
  const renderColumn = useCallback((provided, snapshot) => {
    return /*#__PURE__*/_jsx(StyledColumnContainer, {
      isBoardEmpty: isBoardEmpty,
      "data-test-id": "cdb-column-container",
      collapsed: collapsed,
      children: /*#__PURE__*/_jsx(ScrollingColumn, {
        headerContent: /*#__PURE__*/_jsx(ColumnHeaderWrapper, {
          columnData: columnData,
          collapsed: collapsed,
          onColumnCollapse: onCollapse,
          onHovered: handleHover,
          isBoardEmpty: isBoardEmpty,
          backgroundColor: columnBackgroundColor(snapshot.isDraggingOver)
        }),
        footerContent: collapsed ? undefined : footer,
        "data-test-id": "cdb-column",
        children: /*#__PURE__*/_jsx(StyleColumnBody, Object.assign({
          "data-test-id": "cdb-column-body",
          ref: provided.innerRef
        }, provided.droppableProps, {
          columnBodyBackgroundColor: columnBodyBackgroundColor(snapshot.isDraggingOver),
          children: shouldCollapse ? /*#__PURE__*/_jsx(CollapsedColumnContent, {
            columnData: columnData
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ColumnContent, {
              columnData: columnData,
              shouldRenderCards: shouldRenderCards
            }), provided.placeholder, loadMoreComponent]
          })
        }))
      })
    });
  }, [columnData, collapsed, isBoardEmpty, columnBackgroundColor, columnBodyBackgroundColor, shouldCollapse, shouldRenderCards, loadMoreComponent, footer, onCollapse, handleHover]);
  return isBoardEmpty ? /*#__PURE__*/_jsx(StyledColumnContainer, {
    isBoardEmpty: isBoardEmpty,
    collapsed: collapsed,
    children: /*#__PURE__*/_jsx(ScrollingColumn, {
      headerContent: /*#__PURE__*/_jsx(ColumnHeaderWrapper, {
        columnData: columnData,
        collapsed: collapsed,
        onColumnCollapse: onCollapse,
        onHovered: handleHover,
        isBoardEmpty: isBoardEmpty,
        backgroundColor: columnBackgroundColor(false)
      }),
      "data-test-id": "cdb-column-empty",
      maxInlineSize: "initial"
    })
  }) : /*#__PURE__*/_jsx(Droppable, {
    droppableId: columnData.id,
    type: DND_TYPES.BOARD_CARD,
    children: renderColumn
  });
};
export default Column;